import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ClipboardCheck, Search, Plus, Filter, X, Settings as SettingsIcon, AlertTriangle, Pencil, Target } from 'lucide-react';
import { neonQuery } from '../../services/api';
import { getCurrentUser } from '../../services/auth';
import TaskList from './TaskList';
import TaskDetail from './TaskDetail';
import { FIXED_VIEWS, EPIC_TAB_STATUSES, BACKLOG_STATUSES, STATUSES, PRIORITIES } from './constants';

// View id encoding: dynamic Epic tabs use 'epic-{id}'; fixed views use their own id.
const epicViewId = (epicId) => `epic-${epicId}`;
const parseEpicView = (view) => {
  const m = /^epic-(\d+)$/.exec(view || '');
  return m ? Number(m[1]) : null;
};

export default function Tasks({ initialTaskId }) {
  const user = getCurrentUser();
  const userRoles = Array.isArray(user?.roles) ? user.roles : (user?.roles || '').split(',').map(r => r.trim());
  const isAllowed = userRoles.includes('Admin') || userRoles.includes('IT') ||
                    userRoles.includes('Manager') || userRoles.includes('Marketing') ||
                    userRoles.includes('HR') || userRoles.includes('Procurement') ||
                    userRoles.includes('Fulfillment') || userRoles.includes('Customer Service') ||
                    userRoles.includes('Accounting');

  const [view, setView] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('view') || ''; // resolved to first epic on load if blank
  });
  const [tasks, setTasks] = useState([]);
  const [areas, setAreas] = useState([]);
  const [epics, setEpics] = useState([]);
  const [users, setUsers] = useState([]); // assignable users
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(initialTaskId || null);
  const [search, setSearch] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [filterEpic, setFilterEpic] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [showAreaManager, setShowAreaManager] = useState(false);
  const [showEpicManager, setShowEpicManager] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const loadAreas = useCallback(async () => {
    try {
      const rows = await neonQuery('cp_task_areas_list', { includeArchived: false });
      setAreas(rows || []);
    } catch (e) { console.error(e); }
  }, []);

  const loadEpics = useCallback(async () => {
    try {
      const rows = await neonQuery('cp_task_epics_list', { includeArchived: false });
      setEpics(rows || []);
      return rows || [];
    } catch (e) { console.error(e); return []; }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const rows = await neonQuery('users_list');
      const assignable = (rows || []).filter(u => {
        const r = Array.isArray(u.roles) ? u.roles : (u.roles || '').split(',').map(x => x.trim());
        return u.is_active !== '0' && (
          r.includes('Admin') || r.includes('IT') || r.includes('Manager') ||
          r.includes('Marketing') || r.includes('HR') || r.includes('Procurement') ||
          r.includes('Fulfillment') || r.includes('Customer Service') || r.includes('Accounting')
        );
      });
      setUsers(assignable.map(u => ({ admin_id: u.admin_id, staff_name: u.staff_name })));
    } catch (e) { console.error(e); }
  }, []);

  const loadTasks = useCallback(async () => {
    if (!view) return;
    setLoading(true);
    try {
      const params = { parentTaskId: null }; // top-level only
      const epicId = parseEpicView(view);
      if (epicId !== null) {
        params.epicId = epicId;
        params.statuses = EPIC_TAB_STATUSES;
      } else if (view === 'backlog') params.statuses = BACKLOG_STATUSES;
      else if (view === 'mine') {
        params.mineUserId = user?.id;
        params.statuses = ['planned', 'in_progress', 'blocked', 'review'];
      } else if (view === 'all') {
        params.includeDone = true;
      }
      const rows = await neonQuery('cp_tasks_list', params);
      setTasks(rows || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [view, user?.id]);

  useEffect(() => {
    if (!isAllowed) return;
    loadAreas();
    loadUsers();
    // Resolve initial view: prefer URL param, else fall through to first epic.
    loadEpics().then((list) => {
      if (!view) {
        const initial = list[0] ? epicViewId(list[0].id) : 'backlog';
        setView(initial);
      }
    });
  }, [isAllowed, loadAreas, loadUsers, loadEpics]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (isAllowed) loadTasks(); }, [isAllowed, loadTasks]);

  // Auto-open task from URL param (e.g. email deep link)
  useEffect(() => {
    if (initialTaskId && isAllowed) {
      setSelectedId(Number(initialTaskId));
      if (window.location.search) window.history.replaceState({}, '', window.location.pathname);
    }
  }, [initialTaskId, isAllowed]);

  const switchView = (v) => {
    setView(v);
    setSelectedId(null);
    const params = new URLSearchParams(window.location.search);
    params.set('view', v);
    window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
  };

  const onQuickAdd = useCallback(async (areaId, title, defaultStatus) => {
    const epicId = parseEpicView(view);
    await neonQuery('cp_tasks_upsert', {
      title,
      area_id: areaId,
      epic_id: epicId,  // sticks new tasks to the active Epic when one is open
      status: defaultStatus || (view === 'backlog' ? 'backlog' : 'planned'),
      priority: 'medium',
      actor_id: user?.id,
      actor_name: user?.staffName,
    });
    await loadTasks();
    await loadEpics();  // active_count badges refresh
  }, [view, user?.id, user?.staffName, loadTasks, loadEpics]);

  const onTaskUpdated = useCallback(async () => {
    await loadTasks();
    await loadEpics();
  }, [loadTasks, loadEpics]);
  const onTaskDeleted = useCallback(async () => {
    setSelectedId(null);
    await loadTasks();
    await loadEpics();
  }, [loadTasks, loadEpics]);

  const overdueCount = useMemo(() => {
    const today0 = new Date(new Date().toDateString());
    return tasks.filter(t => t.due_date && new Date(t.due_date) < today0 && t.status !== 'done').length;
  }, [tasks]);

  if (!isAllowed) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)' }}>
        <ClipboardCheck size={48} style={{ marginBottom: 12, color: 'var(--orange)' }} />
        <h2 style={{ color: 'var(--navy)' }}>Access Restricted</h2>
        <p>Tasks is only available to admins.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px 0 20px', flex: '0 0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <ClipboardCheck size={22} color="var(--navy)" />
          <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Tasks</h1>
          <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
            Project tracker · backlog, roadmap, milestones
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button
              onClick={() => setShowEpicManager(true)}
              title="Manage epics"
              style={{ padding: '6px 10px', background: 'transparent', border: '1px solid var(--gray-300)', borderRadius: 6, cursor: 'pointer', color: 'var(--gray-700)', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Target size={12} /> Epics
            </button>
            <button
              onClick={() => setShowAreaManager(true)}
              title="Manage areas"
              style={{ padding: '6px 10px', background: 'transparent', border: '1px solid var(--gray-300)', borderRadius: 6, cursor: 'pointer', color: 'var(--gray-700)', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <SettingsIcon size={12} /> Areas
            </button>
            <button
              onClick={() => setShowFilters(v => !v)}
              style={{ padding: '6px 10px', background: showFilters ? 'var(--navy)' : 'transparent', color: showFilters ? '#fff' : 'var(--gray-700)', border: '1px solid var(--gray-300)', borderRadius: 6, cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Filter size={12} /> Filters
            </button>
          </div>
        </div>

        {/* View tabs — Epics first, then a divider, then fixed views */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, borderBottom: '1px solid var(--gray-200)', flexWrap: 'wrap' }}>
          {epics.map(ep => {
            const id = epicViewId(ep.id);
            const isActive = view === id;
            return (
              <button
                key={id}
                onClick={() => switchView(id)}
                title={ep.description || ep.name}
                style={{
                  padding: '8px 14px', background: 'transparent', border: 'none',
                  borderBottom: isActive ? `2px solid ${ep.color || 'var(--orange)'}` : '2px solid transparent',
                  color: isActive ? (ep.color || 'var(--orange)') : 'var(--gray-700)',
                  fontWeight: isActive ? 700 : 600, fontSize: 13, cursor: 'pointer',
                  marginBottom: -1, display: 'inline-flex', alignItems: 'center', gap: 6,
                }}>
                {ep.color && <span style={{ width: 8, height: 8, borderRadius: '50%', background: ep.color, display: 'inline-block' }} />}
                {ep.name}
                {ep.active_count > 0 && (
                  <span style={{
                    fontSize: 10, padding: '1px 6px', borderRadius: 8,
                    background: isActive ? (ep.color || 'var(--orange)') : 'var(--gray-200)',
                    color: isActive ? '#fff' : 'var(--gray-700)', fontWeight: 600,
                  }}>{ep.active_count}</span>
                )}
              </button>
            );
          })}
          <button
            onClick={() => setShowEpicManager(true)}
            title="Add epic"
            style={{ padding: '8px 10px', background: 'transparent', border: 'none', color: 'var(--gray-500)', fontSize: 13, cursor: 'pointer', marginBottom: -1, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <Plus size={13} />
          </button>
          <div style={{ width: 1, height: 22, background: 'var(--gray-200)', margin: '0 8px 6px 8px' }} />
          {FIXED_VIEWS.map(v => {
            const isActive = view === v.id;
            return (
              <button
                key={v.id}
                onClick={() => switchView(v.id)}
                title={v.hint}
                style={{
                  padding: '8px 14px', background: 'transparent', border: 'none',
                  borderBottom: isActive ? '2px solid var(--orange)' : '2px solid transparent',
                  color: isActive ? 'var(--orange)' : 'var(--gray-600)',
                  fontWeight: isActive ? 700 : 500, fontSize: 13, cursor: 'pointer',
                  marginBottom: -1,
                }}>
                {v.label}
              </button>
            );
          })}
          {overdueCount > 0 && (
            <div style={{ marginLeft: 'auto', padding: '8px 12px', display: 'inline-flex', alignItems: 'center', gap: 5, color: '#dc2626', fontSize: 12, fontWeight: 600 }}>
              <AlertTriangle size={13} /> {overdueCount} overdue
            </div>
          )}
        </div>

        {/* Filters bar */}
        {showFilters && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '10px 0' }}>
            <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 180 }}>
              <Search size={13} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)' }} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search title or description"
                style={{ padding: '7px 10px 7px 28px', width: '100%', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12.5, outline: 'none' }}
              />
            </div>
            <select value={filterArea} onChange={e => setFilterArea(e.target.value)} style={selStyle}>
              <option value="">All areas</option>
              {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <select value={filterEpic} onChange={e => setFilterEpic(e.target.value)} style={selStyle}>
              <option value="">Any epic</option>
              <option value="0">No epic</option>
              {epics.map(ep => <option key={ep.id} value={ep.id}>{ep.name}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selStyle}>
              <option value="">Any status</option>
              {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} style={selStyle}>
              <option value="">Any priority</option>
              {PRIORITIES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            <select value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)} style={selStyle}>
              <option value="">Any assignee</option>
              <option value="0">Unassigned</option>
              {users.map(u => <option key={u.admin_id} value={u.admin_id}>{u.staff_name}</option>)}
            </select>
            {(search || filterArea || filterEpic || filterStatus || filterPriority || filterAssignee) && (
              <button
                onClick={() => { setSearch(''); setFilterArea(''); setFilterEpic(''); setFilterStatus(''); setFilterPriority(''); setFilterAssignee(''); }}
                style={{ padding: '7px 10px', background: 'transparent', border: '1px solid var(--gray-300)', borderRadius: 6, cursor: 'pointer', fontSize: 12, color: 'var(--gray-600)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                <X size={12} /> Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* Master / Detail */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0, borderTop: '1px solid var(--gray-200)' }}>
        <div style={{ flex: '0 0 420px', borderRight: '1px solid var(--gray-200)', overflowY: 'auto' }}>
          {loading
            ? <div style={{ padding: 40, color: 'var(--gray-500)', textAlign: 'center' }}>Loading…</div>
            : <TaskList
                view={view}
                tasks={tasks}
                areas={areas}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onQuickAdd={onQuickAdd}
                filterArea={filterArea}
                filterEpic={filterEpic}
                filterStatus={filterStatus}
                filterPriority={filterPriority}
                filterAssignee={filterAssignee}
                search={search}
              />}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', background: '#fff' }}>
          <TaskDetail
            taskId={selectedId}
            areas={areas}
            epics={epics}
            assignableUsers={users}
            onUpdated={onTaskUpdated}
            onDeleted={onTaskDeleted}
          />
        </div>
      </div>

      {showAreaManager && (
        <AreaManager
          areas={areas}
          onClose={() => setShowAreaManager(false)}
          onChanged={() => { loadAreas(); loadTasks(); }}
        />
      )}
      {showEpicManager && (
        <EpicManager
          epics={epics}
          onClose={() => setShowEpicManager(false)}
          onChanged={() => { loadEpics(); loadTasks(); }}
        />
      )}
    </div>
  );
}

const selStyle = { padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12.5, outline: 'none', background: '#fff' };

function EpicManager({ epics, onClose, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#DC582A');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => { setEditing(null); setName(''); setColor('#DC582A'); setDescription(''); };
  const startEdit = (ep) => { setEditing(ep); setName(ep.name); setColor(ep.color || '#DC582A'); setDescription(ep.description || ''); };

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await neonQuery('cp_task_epics_upsert', {
        id: editing?.id || null,
        name: name.trim(),
        color: color || null,
        description: description.trim() || null,
        sort_order: editing?.sort_order ?? ((epics.length + 1) * 10),
      });
      reset();
      onChanged?.();
    } catch (e) { alert('Save failed: ' + e.message); }
    finally { setSaving(false); }
  };

  const archive = async (ep) => {
    if (!window.confirm(`Archive "${ep.name}"? Tasks remain but the epic is hidden from the tab strip and from new task creation.`)) return;
    await neonQuery('cp_task_epics_archive', { id: ep.id, archived: true });
    onChanged?.();
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, padding: 24, width: 600, maxHeight: '85vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <Target size={18} color="var(--navy)" />
          <h2 style={{ margin: '0 0 0 8px', fontSize: 16, fontWeight: 700, color: 'var(--navy)' }}>Manage Epics</h2>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>
        <p style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 0, marginBottom: 14, lineHeight: 1.5 }}>
          Each Epic is a defined goal — surfaces as a top-level tab on the Tasks page. The first Epic loads by default. Tasks can be assigned to an Epic from the detail panel; new tasks added inside an Epic tab inherit that Epic.
        </p>
        <div style={{ marginBottom: 14 }}>
          {epics.map(ep => (
            <div key={ep.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--gray-100)' }}>
              <span style={{ width: 12, height: 12, borderRadius: '50%', background: ep.color || 'var(--gray-300)', display: 'inline-block', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{ep.name}</div>
                {ep.description && <div style={{ fontSize: 11, color: 'var(--gray-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ep.description}</div>}
              </div>
              <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                {ep.active_count} active · {ep.done_count} done
              </span>
              <button onClick={() => startEdit(ep)} style={{ background: 'transparent', border: '1px solid var(--gray-300)', borderRadius: 4, padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}>Edit</button>
              <button onClick={() => archive(ep)} style={{ background: 'transparent', border: '1px solid var(--gray-300)', borderRadius: 4, padding: '3px 8px', fontSize: 11, cursor: 'pointer', color: '#dc2626' }}>Archive</button>
            </div>
          ))}
          {!epics.length && <div style={{ padding: 12, color: 'var(--gray-500)', fontSize: 13 }}>No Epics yet — add the first one below.</div>}
        </div>
        <div style={{ background: '#fafbfc', padding: 12, borderRadius: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--gray-500)', marginBottom: 8 }}>
            {editing ? `Edit "${editing.name}"` : 'Add new Epic'}
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder='Name (e.g. "Phase 2", "Q3 Marketing")'
              style={{ flex: '1 1 200px', padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12.5, outline: 'none' }}
            />
            <input
              type="color"
              value={color || '#DC582A'}
              onChange={e => setColor(e.target.value)}
              title="Pick a color"
              style={{ width: 36, height: 32, padding: 0, border: '1px solid var(--gray-300)', borderRadius: 6, cursor: 'pointer' }}
            />
          </div>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Description (optional) — what defines this goal?"
            style={{ width: '100%', minHeight: 50, padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: 8 }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={save}
              disabled={saving || !name.trim()}
              style={{ padding: '7px 14px', background: saving ? 'var(--gray-300)' : 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: saving ? 'wait' : 'pointer' }}>
              {editing ? 'Save' : 'Add Epic'}
            </button>
            {editing && (
              <button onClick={reset} style={{ padding: '7px 12px', background: 'transparent', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12, color: 'var(--gray-600)', cursor: 'pointer' }}>Cancel</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AreaManager({ areas, onClose, onChanged }) {
  const [editing, setEditing] = useState(null); // { id, name, color, sort_order }
  const [name, setName] = useState('');
  const [color, setColor] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => { setEditing(null); setName(''); setColor(''); };
  const startEdit = (a) => { setEditing(a); setName(a.name); setColor(a.color || ''); };

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await neonQuery('cp_task_areas_upsert', {
        id: editing?.id || null,
        name: name.trim(),
        color: color || null,
        sort_order: editing?.sort_order ?? (areas.length * 10),
      });
      reset();
      onChanged?.();
    } catch (e) { alert('Save failed: ' + e.message); }
    finally { setSaving(false); }
  };

  const archive = async (a) => {
    if (!window.confirm(`Archive "${a.name}"? Tasks remain but the area is hidden from new task creation.`)) return;
    await neonQuery('cp_task_areas_archive', { id: a.id, archived: true });
    onChanged?.();
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, padding: 24, width: 540, maxHeight: '80vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--navy)' }}>Manage Areas</h2>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ marginBottom: 14 }}>
          {areas.map(a => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--gray-100)' }}>
              <span style={{ width: 12, height: 12, borderRadius: '50%', background: a.color || 'var(--gray-300)', display: 'inline-block', flexShrink: 0 }} />
              <span style={{ flex: 1, fontWeight: 600, color: 'var(--navy)' }}>{a.name}</span>
              <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{a.task_count} task{a.task_count === 1 ? '' : 's'}</span>
              <button onClick={() => startEdit(a)} style={{ background: 'transparent', border: '1px solid var(--gray-300)', borderRadius: 4, padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}>Edit</button>
              <button onClick={() => archive(a)} style={{ background: 'transparent', border: '1px solid var(--gray-300)', borderRadius: 4, padding: '3px 8px', fontSize: 11, cursor: 'pointer', color: '#dc2626' }}>Archive</button>
            </div>
          ))}
          {!areas.length && <div style={{ padding: 12, color: 'var(--gray-500)', fontSize: 13 }}>No areas yet — add the first one below.</div>}
        </div>
        <div style={{ background: '#fafbfc', padding: 12, borderRadius: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--gray-500)', marginBottom: 8 }}>
            {editing ? `Edit "${editing.name}"` : 'Add new area'}
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Name (e.g. PDP, Checkout)"
              style={{ flex: '1 1 200px', padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12.5, outline: 'none' }}
            />
            <input
              type="color"
              value={color || '#cccccc'}
              onChange={e => setColor(e.target.value)}
              title="Pick a color"
              style={{ width: 36, height: 32, padding: 0, border: '1px solid var(--gray-300)', borderRadius: 6, cursor: 'pointer' }}
            />
            <button
              onClick={save}
              disabled={saving || !name.trim()}
              style={{ padding: '7px 14px', background: saving ? 'var(--gray-300)' : 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: saving ? 'wait' : 'pointer' }}>
              {editing ? 'Save' : 'Add'}
            </button>
            {editing && (
              <button onClick={reset} style={{ padding: '7px 12px', background: 'transparent', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12, color: 'var(--gray-600)', cursor: 'pointer' }}>Cancel</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
